import { useEffect, useState, useRef, useMemo } from "react";
import { api, fmtDate, downloadFile, fileUrl, scadenzaInfo, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Award, Download, Building2, GraduationCap, CalendarClock,
  CheckCircle2, Circle, Upload, FileText, X, Lock, FileArchive, Plus, Paperclip, Trash2, Pencil, Sparkles, IdCard, Search, ChevronDown, ChevronRight,
} from "lucide-react";

const DOCS = [
  ["consegna_attestato", "Consegna attestato"],
  ["verbale_finale", "Verbale finale"],
  ["test", "Test"],
  ["registro", "Registro"],
  ["dichiarazione", "Dichiarazione"],
  ["report", "Report"],
  ["monitoraggio", "Monitoraggio"],
];

function DocRow({ a, tipo, label, templatePath, templateName, isPartner, onUpload, onRemove, busy }) {
  const ref = a.documenti?.[tipo];
  const inputRef = useRef(null);
  const key = `${a.order_id}-${a.ri}-${a.ci}-${tipo}`;
  return (
    <div className="flex items-center justify-between gap-2 text-sm py-1" data-testid={`doc-row-${key}`}>
      <span className="flex items-center gap-2 min-w-0">
        {ref ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" /> : <Circle className="h-4 w-4 text-slate-300 shrink-0" />}
        <span className={`truncate ${ref ? "text-slate-700" : "text-slate-400"}`}>{label}</span>
        {templatePath ? (
          <a href={fileUrl(templatePath)} target="_blank" rel="noreferrer" title={`Scarica modulo: ${templateName || "Modulo"}`}
            className="inline-flex items-center gap-0.5 text-[11px] text-dusty-600 hover:text-dusty-700 shrink-0" data-testid={`doc-template-${key}`}>
            <Download className="h-3 w-3" /> modulo
          </a>
        ) : null}
      </span>
      <span className="flex items-center gap-1.5 shrink-0">
        {ref ? (
          <>
            <a href={fileUrl(ref.storage_path)} target="_blank" rel="noreferrer" title="Visualizza"
              className="text-dusty-600 hover:text-dusty-700" data-testid={`doc-view-${key}`}><FileText className="h-4 w-4" /></a>
            <button onClick={() => onRemove(a, tipo)} title="Rimuovi" className="text-slate-400 hover:text-rose-500" data-testid={`doc-remove-${key}`}><X className="h-4 w-4" /></button>
          </>
        ) : (
          <>
            <input ref={inputRef} type="file" className="hidden" data-testid={`doc-input-${key}`}
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUpload(a, tipo, f); }} />
            <Button size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()}
              className="h-7 px-2 text-xs" data-testid={`doc-upload-${key}`}>
              <Upload className="h-3.5 w-3.5 mr-1" /> Carica
            </Button>
          </>
        )}
      </span>
    </div>
  );
}

export default function Attestati() {
  const { user } = useAuth();
  const isOwner = user.role !== "partner";
  const isPartner = user.role === "partner";
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [manualItems, setManualItems] = useState([]);
  const [partners, setPartners] = useState([]);
  const [mDialog, setMDialog] = useState(false);
  const [mBusy, setMBusy] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [mEditId, setMEditId] = useState(null);
  const [mSearch, setMSearch] = useState("");
  const [attSearch, setAttSearch] = useState("");
  const [attTag, setAttTag] = useState("all");
  const emptyM = { corsista_nome: "", codice_fiscale: "", corso_nome: "", azienda_nome: "", data_rilascio: "", scadenza: "", partner_id: "none", file: null, allegati: [] };
  const [mForm, setMForm] = useState(emptyM);

  const CATS = [["aula", "Aula"], ["videoconferenza", "Videoconferenza"], ["elearning", "FAD asincrona"], ["consulenza", "Consulenza"]];
  const [docCfgOpen, setDocCfgOpen] = useState(false);
  const [docCfg, setDocCfg] = useState(null);
  const [docCfgBusy, setDocCfgBusy] = useState(false);
  const openDocCfg = () => {
    api.get("/doc-config").then((r) => { setDocCfg(r.data.categorie); setDocCfgOpen(true); }).catch(() => toast.error("Impossibile caricare la configurazione"));
  };
  const setDocLabel = (cat, i, v) => setDocCfg((c) => ({ ...c, [cat]: c[cat].map((d, idx) => (idx === i ? { ...d, label: v } : d)) }));
  const addDoc = (cat) => setDocCfg((c) => ({ ...c, [cat]: [...(c[cat] || []), { key: "", label: "" }] }));
  const removeCfgDoc = (cat, i) => setDocCfg((c) => ({ ...c, [cat]: c[cat].filter((_, idx) => idx !== i) }));
  const uploadTemplate = async (cat, i, file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocCfg((c) => ({ ...c, [cat]: c[cat].map((d, idx) => (idx === i ? { ...d, template_path: r.data.storage_path, template_name: r.data.original_filename } : d)) }));
      toast.success("Modulo caricato. Ricorda di salvare la configurazione.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeTemplate = (cat, i) => setDocCfg((c) => ({ ...c, [cat]: c[cat].map((d, idx) => (idx === i ? { ...d, template_path: "", template_name: "" } : d)) }));

  const FMT_FIELDS = [["nome", "Nome"], ["cognome", "Cognome"], ["codice_fiscale", "Codice Fiscale"], ["azienda", "Azienda"], ["corso", "Corso"], ["data", "Data corso"], ["ore", "Ore"], ["modalita", "Modalità"], ["numero_attestato", "N° Attestato"], ["data_rilascio", "Data rilascio"], ["scadenza", "Scadenza"]];
  const emptyFmt = { nome: "", categoria: "tutte", corpo: "" };
  const [formats, setFormats] = useState([]);
  const [fmtOpen, setFmtOpen] = useState(false);
  const [fmtForm, setFmtForm] = useState(emptyFmt);
  const [fmtEditId, setFmtEditId] = useState(null);
  const [genFor, setGenFor] = useState(null);
  const [genFields, setGenFields] = useState({});
  const openFormats = () => { api.get("/doc-formats").then((r) => { setFormats(r.data); setFmtForm(emptyFmt); setFmtEditId(null); setFmtOpen(true); }).catch(() => toast.error("Impossibile caricare i format")); };
  const loadFormats = () => api.get("/doc-formats").then((r) => setFormats(r.data)).catch(() => {});
  const editFmt = (f) => { setFmtForm({ nome: f.nome, categoria: f.categoria || "tutte", corpo: f.corpo || "" }); setFmtEditId(f.id); };
  const insertField = (k) => setFmtForm((f) => ({ ...f, corpo: (f.corpo || "") + `{{${k}}}` }));
  const saveFmt = async () => {
    if (!fmtForm.nome.trim()) return toast.error("Indica un nome per il format");
    try {
      if (fmtEditId) await api.put(`/doc-formats/${fmtEditId}`, fmtForm);
      else await api.post("/doc-formats", fmtForm);
      toast.success("Format salvato"); setFmtForm(emptyFmt); setFmtEditId(null); loadFormats();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delFmt = async (id) => { if (!window.confirm("Eliminare questo format?")) return; await api.delete(`/doc-formats/${id}`); toast.success("Format eliminato"); if (fmtEditId === id) { setFmtForm(emptyFmt); setFmtEditId(null); } loadFormats(); };
  const openGen = (f) => { setGenFor(f); setGenFields({}); };
  const generatePdf = async () => {
    try {
      const r = await api.post(`/doc-formats/${genFor.id}/genera`, { campi: genFields }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `${genFor.nome}.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success("PDF generato"); setGenFor(null);
    } catch (e) { toast.error("Errore nella generazione del PDF"); }
  };
  const saveDocCfg = async () => {
    setDocCfgBusy(true);
    try {
      const r = await api.put("/doc-config", { categorie: docCfg });
      setDocCfg(r.data.categorie);
      toast.success("Documenti richiesti aggiornati");
      setDocCfgOpen(false);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDocCfgBusy(false); }
  };

  const reload = () => api.get("/attestati").then((r) => setItems(r.data)).catch(() => {});
  const reloadManual = () => api.get("/attestati/manuali").then((r) => setManualItems(r.data)).catch(() => {});
  useEffect(() => {
    reload();
    if (isOwner) { reloadManual(); api.get("/partners").then((r) => setPartners(r.data)).catch(() => {}); }
    if (isPartner) api.put("/notifications/read").then(() => window.dispatchEvent(new Event("badges-refresh"))).catch(() => {});
  }, [isPartner, isOwner]);

  const openNewManual = () => { setMForm(emptyM); setMEditId(null); setMDialog(true); };
  const openEditManual = (m) => {
    setMForm({
      corsista_nome: m.corsista_nome === "—" ? "" : (m.corsista_nome || ""), codice_fiscale: m.codice_fiscale || "",
      corso_nome: m.corso_nome || "", azienda_nome: m.azienda_nome || "",
      data_rilascio: m.data_rilascio || "", scadenza: m.scadenza || "",
      partner_id: m.partner_id || "none", file: null, allegati: [],
    });
    setMEditId(m.id); setMDialog(true);
  };

  const onManualFile = async (f) => {
    setMForm((m) => ({ ...m, file: f }));
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/attestati/analizza", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data || {};
      const nomeCognome = [d.nome, d.cognome].filter(Boolean).join(" ").trim();
      setMForm((m) => ({
        ...m,
        corsista_nome: nomeCognome || m.corsista_nome,
        codice_fiscale: d.codice_fiscale || m.codice_fiscale,
        data_rilascio: d.data_rilascio || m.data_rilascio,
        scadenza: d.scadenza || m.scadenza,
      }));
      if (nomeCognome || d.codice_fiscale || d.scadenza) toast.success("Dati riconosciuti dall'attestato");
      else toast.info("Non sono riuscito a leggere i dati: compilali a mano");
    } catch (e) { toast.error("Riconoscimento non riuscito, compila i dati a mano"); }
    finally { setAnalyzing(false); }
  };

  const submitManual = async () => {
    if (!mEditId && !mForm.file) return toast.error("Seleziona il file dell'attestato");
    if (!mForm.corsista_nome.trim() && !mForm.corso_nome.trim()) return toast.error("Indica almeno il corsista o il corso");
    setMBusy(true);
    try {
      const fd = new FormData();
      if (mForm.file) fd.append("file", mForm.file);
      (mForm.allegati || []).forEach((f) => fd.append("allegati", f));
      fd.append("corsista_nome", mForm.corsista_nome);
      fd.append("codice_fiscale", mForm.codice_fiscale);
      fd.append("corso_nome", mForm.corso_nome);
      fd.append("azienda_nome", mForm.azienda_nome);
      fd.append("data_rilascio", mForm.data_rilascio);
      fd.append("scadenza", mForm.scadenza);
      fd.append("partner_id", mForm.partner_id === "none" ? "" : mForm.partner_id);
      if (mEditId) await api.put(`/attestati/manuali/${mEditId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      else await api.post("/attestati/manuali", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(mEditId ? "Attestato aggiornato" : "Attestato caricato");
      setMDialog(false); setMForm(emptyM); setMEditId(null); reloadManual();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setMBusy(false); }
  };
  const deleteManual = async (m) => {
    if (!window.confirm("Eliminare questo attestato manuale?")) return;
    try { await api.delete(`/attestati/manuali/${m.id}`); reloadManual(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const downloadManual = async (m) => {
    try { await downloadFile(`/attestati/manuali/${m.id}/download`, m.attestato?.original_filename || `Attestato - ${m.corsista_nome}.pdf`); }
    catch (e) { toast.error("Download non riuscito"); }
  };

  const uploadDoc = async (a, tipo, file) => {
    if (file.size > 10 * 1024 * 1024) return toast.error("File troppo grande (max 10 MB)");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/orders/${a.order_id}/righe/${a.ri}/corsisti/${a.ci}/documenti/${tipo}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const removeDoc = async (a, tipo) => {
    try { await api.delete(`/orders/${a.order_id}/righe/${a.ri}/corsisti/${a.ci}/documenti/${tipo}`); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const scarica = async (a) => {
    try {
      await downloadFile(`/orders/${a.order_id}/righe/${a.ri}/corsisti/${a.ci}/attestato/download`, `Attestato - ${a.corsista_nome}.pdf`);
    } catch (e) {
      toast.error(e.response?.status === 403 ? "Carica prima tutti i documenti richiesti" : "Download non riuscito");
    }
  };
  const scaricaZip = async (a) => {
    try {
      await downloadFile(`/orders/${a.order_id}/righe/${a.ri}/corsisti/${a.ci}/pacchetto.zip`, `Pacchetto - ${a.corsista_nome}.zip`);
    } catch (e) {
      toast.error("Download pacchetto non riuscito");
    }
  };

  const scadStatus = (a) => {
    const scad = a?.attestato?.scadenza;
    if (!scad) return "valido";
    const info = scadenzaInfo(scad);
    if (!info) return "valido";
    if (info.days < 0) return "scaduto";
    if (info.days <= 30) return "scadenza";
    return "valido";
  };

  const filteredItems = useMemo(() => {
    const q = attSearch.trim().toLowerCase();
    return items.filter((a) => {
      const matchQ = !q || [a.corsista_nome, a.corso_nome, a.azienda_nome, a.codice_fiscale, a.partner_nome]
        .some((v) => (v || "").toLowerCase().includes(q));
      const matchTag = attTag === "all" || scadStatus(a) === attTag;
      return matchQ && matchTag;
    });
  }, [items, attSearch, attTag]);

  const tagCounts = useMemo(() => {
    const c = { all: items.length, valido: 0, scadenza: 0, scaduto: 0 };
    items.forEach((a) => { c[scadStatus(a)] += 1; });
    return c;
  }, [items]);

  const ATT_TAGS = [
    ["all", "Tutti"], ["valido", "Validi"], ["scadenza", "In scadenza"], ["scaduto", "Scaduti"],
  ];

  const [openGroups, setOpenGroups] = useState(() => new Set());
  const expandAll = attSearch.trim() !== "" || attTag !== "all";
  const toggleGroup = (key) => setOpenGroups((s) => {
    const n = new Set(s);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });

  // Riepilogo scadenze per un gruppo di attestati
  const groupBadges = (arr) => {
    const c = { scaduto: 0, scadenza: 0 };
    arr.forEach((a) => { const s = scadStatus(a); if (s === "scaduto") c.scaduto += 1; else if (s === "scadenza") c.scadenza += 1; });
    return c;
  };

  // Partner: raggruppa per azienda. Owner: raggruppa per partner → azienda.
  const groupedByAzienda = useMemo(() => {
    const by = {};
    filteredItems.forEach((a) => {
      const az = a.azienda_nome || "Senza azienda";
      (by[az] = by[az] || []).push(a);
    });
    return Object.entries(by).sort((x, y) => x[0].localeCompare(y[0])).map(([azienda, items]) => ({ azienda, items }));
  }, [filteredItems]);

  const groupedByPartner = useMemo(() => {
    const by = {};
    filteredItems.forEach((a) => {
      const p = a.partner_nome || "Senza partner";
      const az = a.azienda_nome || "Senza azienda";
      by[p] = by[p] || {};
      (by[p][az] = by[p][az] || []).push(a);
    });
    return Object.entries(by).sort((x, y) => x[0].localeCompare(y[0])).map(([partner, azs]) => ({
      partner,
      total: Object.values(azs).reduce((s, arr) => s + arr.length, 0),
      aziende: Object.entries(azs).sort((x, y) => x[0].localeCompare(y[0])).map(([azienda, items]) => ({ azienda, items })),
    }));
  }, [filteredItems]);

  const renderCard = (a) => {
    if (a.manuale) {
      return (
        <Card key={`m-${a.id}`} className="p-5 border-slate-200 shadow-sm flex flex-col" data-testid={`attestato-manuale-${a.id}`}>
          {(a.attestato?.thumb_path || (a.attestato?.content_type || "").startsWith("image/")) && (
            <a href={fileUrl(a.attestato.storage_path)} target="_blank" rel="noreferrer" className="block mb-3 rounded-lg overflow-hidden border border-slate-200 bg-slate-50 aspect-[1.414/1]" title="Apri l'attestato" data-testid={`attestato-manuale-preview-${a.id}`}>
              <img loading="lazy" decoding="async" src={fileUrl(a.attestato.thumb_path || a.attestato.storage_path)} alt={a.corsista_nome} className="h-full w-full object-cover object-top" />
            </a>
          )}
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0"><Award className="h-5 w-5" /></div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-800 truncate">{a.corsista_nome}</div>
              {a.corso_nome && <div className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5"><GraduationCap className="h-3.5 w-3.5 shrink-0" />{a.corso_nome}</div>}
              {a.azienda_nome && <div className="text-xs text-slate-400 flex items-center gap-1.5 mt-0.5"><Building2 className="h-3 w-3 shrink-0" />{a.azienda_nome}</div>}
            </div>
          </div>
          {a.attestato.scadenza && scadenzaInfo(a.attestato.scadenza) && (
            <div className="mt-3 flex items-center gap-2 text-xs">
              <CalendarClock className="h-3.5 w-3.5 text-slate-400" />
              <span className="text-slate-500">Scadenza: {fmtDate(a.attestato.scadenza)}</span>
              <span className={`px-1.5 py-0.5 rounded-full border ${scadenzaInfo(a.attestato.scadenza).cls}`}>{scadenzaInfo(a.attestato.scadenza).label}</span>
            </div>
          )}
          <div className="mt-4 flex items-center justify-between gap-2">
            <span className="text-xs text-slate-400">{fmtDate(a.attestato.uploaded_at)}</span>
            <button onClick={() => downloadManual(a)} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-dusty-600 hover:bg-dusty-700 text-white transition-colors" data-testid={`attestato-manuale-scarica-${a.id}`}>
              <Download className="h-4 w-4" /> Scarica
            </button>
          </div>
        </Card>
      );
    }
    const reqDocs = (a.documenti_richiesti && a.documenti_richiesti.length)
      ? a.documenti_richiesti.map((d) => [d.key, d.label, d.template_path, d.template_name])
      : DOCS.map(([k, l]) => [k, l, "", ""]);
    const done = reqDocs.filter(([k]) => a.documenti?.[k]).length;
    const locked = isPartner && !a.sbloccato;
    return (
      <Card key={`${a.order_id}-${a.ri}-${a.ci}`} className="p-5 border-slate-200 shadow-sm flex flex-col" data-testid={`attestato-card-${a.order_id}-${a.ri}-${a.ci}`}>
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
            <Award className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-slate-800 truncate">{a.corsista_nome}</div>
            <div className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5"><GraduationCap className="h-3.5 w-3.5 shrink-0" />{a.corso_nome}</div>
            <div className="text-xs text-slate-400 flex items-center gap-1.5 mt-0.5"><Building2 className="h-3 w-3 shrink-0" />{a.azienda_nome}</div>
            {isOwner && <div className="text-xs text-slate-400 mt-0.5">Partner: {a.partner_nome}</div>}
          </div>
        </div>

        {a.attestato.scadenza && (
          <div className="mt-3 flex items-center gap-2 text-xs">
            <CalendarClock className="h-3.5 w-3.5 text-slate-400" />
            <span className="text-slate-500">Scadenza: {fmtDate(a.attestato.scadenza)}</span>
            <span className={`px-1.5 py-0.5 rounded-full border ${scadenzaInfo(a.attestato.scadenza).cls}`} data-testid={`att-scad-${a.order_id}-${a.ri}-${a.ci}`}>
              {scadenzaInfo(a.attestato.scadenza).label}
            </span>
          </div>
        )}

        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Documentazione richiesta{a.categoria ? ` · ${ {aula: "Aula", videoconferenza: "Videoconferenza", elearning: "FAD asincrona", consulenza: "Consulenza"}[a.categoria] || a.categoria}` : ""}</span>
            <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${done === reqDocs.length ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid={`doc-count-${a.order_id}-${a.ri}-${a.ci}`}>{done}/{reqDocs.length}</span>
          </div>
          <div className="divide-y divide-slate-100">
            {reqDocs.map(([tipo, label, tplPath, tplName]) => (
              <DocRow key={tipo} a={a} tipo={tipo} label={label} templatePath={tplPath} templateName={tplName} isPartner={isPartner} onUpload={uploadDoc} onRemove={removeDoc} busy={busy} />
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-2">
          <span className="text-xs text-slate-400">{fmtDate(a.attestato.uploaded_at)}</span>
          <div className="flex items-center gap-2">
            {isOwner && (
              <button onClick={() => scaricaZip(a)} title="Scarica attestato + documenti in ZIP"
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-dusty-200 text-dusty-700 hover:bg-dusty-50 transition-colors"
                data-testid={`attestato-zip-${a.order_id}-${a.ri}-${a.ci}`}>
                <FileArchive className="h-4 w-4" /> ZIP
              </button>
            )}
            {locked ? (
              <div className="inline-flex flex-col items-end">
                <button disabled className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-slate-100 text-slate-400 cursor-not-allowed" data-testid={`attestato-locked-${a.order_id}-${a.ri}-${a.ci}`}>
                  <Lock className="h-4 w-4" /> Scarica
                </button>
                <span className="text-[11px] text-amber-600 mt-1">Carica tutti i documenti per sbloccare</span>
              </div>
            ) : (
              <button onClick={() => scarica(a)}
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-dusty-600 hover:bg-dusty-700 text-white transition-colors"
                data-testid={`attestato-scarica-${a.order_id}-${a.ri}-${a.ci}`}>
                <Download className="h-4 w-4" /> Scarica
              </button>
            )}
          </div>
        </div>
      </Card>
    );
  };

  const AziendaGroup = ({ azienda, items, groupKey, render = renderCard, forceOpen = false }) => {
    const open = forceOpen || openGroups.has(groupKey);
    const b = groupBadges(items);
    return (
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`azienda-group-${azienda}`}>
        <button onClick={() => toggleGroup(groupKey)} className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left" data-testid={`azienda-group-toggle-${azienda}`}>
          <div className="flex items-center gap-2.5 min-w-0">
            {open ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />}
            <Building2 className="h-4 w-4 text-dusty-600 shrink-0" />
            <span className="font-semibold text-slate-800 truncate">{azienda}</span>
            <span className="text-xs text-slate-400 shrink-0">({items.length})</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {b.scaduto > 0 && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200">{b.scaduto} scaduti</span>}
            {b.scadenza > 0 && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">{b.scadenza} in scadenza</span>}
          </div>
        </button>
        {open && (
          <div className="p-4 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 bg-slate-50/40">
            {items.map(render)}
          </div>
        )}
      </div>
    );
  };

  const PartnerGroups = ({ groups, keyPrefix, render, forceOpen }) => (
    <div className="space-y-3">
      {groups.map((gp) => {
        const pKey = `${keyPrefix}:${gp.partner}`;
        const pOpen = forceOpen || openGroups.has(pKey);
        const pb = groupBadges(gp.aziende.flatMap((x) => x.items));
        return (
          <div key={gp.partner} className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`partner-group-${gp.partner}`}>
            <button onClick={() => toggleGroup(pKey)} className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-dusty-50/60 hover:bg-dusty-50 transition-colors text-left" data-testid={`partner-group-toggle-${gp.partner}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                {pOpen ? <ChevronDown className="h-4 w-4 text-dusty-500 shrink-0" /> : <ChevronRight className="h-4 w-4 text-dusty-500 shrink-0" />}
                <span className="font-display font-semibold text-dusty-800 truncate">{gp.partner}</span>
                <span className="text-xs text-slate-400 shrink-0">{gp.aziende.length} aziende · {gp.total} attestati</span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {pb.scaduto > 0 && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200">{pb.scaduto} scaduti</span>}
                {pb.scadenza > 0 && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">{pb.scadenza} in scadenza</span>}
              </div>
            </button>
            {pOpen && (
              <div className="p-3 border-t border-slate-100 space-y-2.5">
                {gp.aziende.map((ga) => (
                  <AziendaGroup key={ga.azienda} azienda={ga.azienda} items={ga.items} groupKey={`${pKey}|az:${ga.azienda}`} render={render} forceOpen={forceOpen} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // Raggruppa una lista generica per partner → azienda (per attestati manuali)
  const groupByPartnerAzienda = (list) => {
    const by = {};
    list.forEach((m) => {
      const p = m.partner_nome || "Senza partner";
      const az = m.azienda_nome || "Senza azienda";
      by[p] = by[p] || {};
      (by[p][az] = by[p][az] || []).push(m);
    });
    return Object.entries(by).sort((x, y) => x[0].localeCompare(y[0])).map(([partner, azs]) => ({
      partner,
      total: Object.values(azs).reduce((s, arr) => s + arr.length, 0),
      aziende: Object.entries(azs).sort((x, y) => x[0].localeCompare(y[0])).map(([azienda, items]) => ({ azienda, items })),
    }));
  };

  const renderManualCard = (m) => (
    <Card key={m.id} className="p-5 border-slate-200 shadow-sm flex flex-col" data-testid={`manual-card-${m.id}`}>
      {(m.attestato?.thumb_path || (m.attestato?.content_type || "").startsWith("image/")) && (
        <a href={fileUrl(m.attestato.storage_path)} target="_blank" rel="noreferrer" className="block mb-3 rounded-lg overflow-hidden border border-slate-200 bg-slate-50 aspect-[1.414/1]" title="Apri l'attestato" data-testid={`manual-preview-${m.id}`}>
          <img loading="lazy" decoding="async" src={fileUrl(m.attestato.thumb_path || m.attestato.storage_path)} alt={m.corsista_nome} className="h-full w-full object-cover object-top" />
        </a>
      )}
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-lg bg-dusty-50 text-dusty-600 flex items-center justify-center shrink-0">
          <Award className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-slate-800 truncate">{m.corsista_nome}</div>
          {m.codice_fiscale && <div className="text-xs text-slate-400 flex items-center gap-1.5 mt-0.5"><IdCard className="h-3 w-3 shrink-0" />{m.codice_fiscale}</div>}
          {m.corso_nome && <div className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5"><GraduationCap className="h-3.5 w-3.5 shrink-0" />{m.corso_nome}</div>}
          {m.azienda_nome && <div className="text-xs text-slate-400 flex items-center gap-1.5 mt-0.5"><Building2 className="h-3 w-3 shrink-0" />{m.azienda_nome}</div>}
          {m.partner_nome && <div className="text-xs text-dusty-600 mt-0.5">Partner: {m.partner_nome}</div>}
        </div>
        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 shrink-0">Manuale</span>
      </div>
      <div className="mt-3 space-y-1.5 text-xs">
        {m.data_rilascio && (
          <div className="flex items-center gap-2"><CalendarClock className="h-3.5 w-3.5 text-slate-400" /><span className="text-slate-500">Rilascio: {fmtDate(m.data_rilascio)}</span></div>
        )}
        {m.scadenza && scadenzaInfo(m.scadenza) && (
          <div className="flex items-center gap-2">
            <CalendarClock className="h-3.5 w-3.5 text-slate-400" />
            <span className="text-slate-500">Scadenza: {fmtDate(m.scadenza)}</span>
            <span className={`px-1.5 py-0.5 rounded-full border ${scadenzaInfo(m.scadenza).cls}`}>{scadenzaInfo(m.scadenza).label}</span>
          </div>
        )}
      </div>
      {(m.allegati || []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {m.allegati.map((a) => (
            <a key={a.id} href={fileUrl(a.storage_path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50" data-testid={`manual-allegato-${a.id}`}><FileText className="h-3 w-3" />{a.original_filename}</a>
          ))}
        </div>
      )}
      <div className="mt-4 flex items-center justify-between gap-2">
        <span className="text-xs text-slate-400">{fmtDate(m.created_at)}</span>
        <div className="flex items-center gap-2">
          <button onClick={() => openEditManual(m)} title="Modifica" className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-500 hover:text-dusty-700 hover:border-dusty-200 transition-colors" data-testid={`manual-edit-${m.id}`}><Pencil className="h-4 w-4" /></button>
          <button onClick={() => deleteManual(m)} title="Elimina" className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-500 hover:text-rose-600 hover:border-rose-200 transition-colors" data-testid={`manual-delete-${m.id}`}><Trash2 className="h-4 w-4" /></button>
          <button onClick={() => downloadManual(m)} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-dusty-600 hover:bg-dusty-700 text-white transition-colors" data-testid={`manual-download-${m.id}`}><Download className="h-4 w-4" /> Scarica</button>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">{isOwner ? "Attestati Caricati" : "I Miei Attestati"}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {isOwner
              ? "Attestati dei corsisti e documentazione firmata, raggruppati per partner e per azienda."
              : "I tuoi attestati raggruppati per azienda: apri un'azienda per vedere tutti i suoi attestati e caricare i documenti richiesti."}
          </p>
        </div>
        {isOwner && (
          <div className="flex items-center gap-2">
            {user.role === "owner" && (
              <Button onClick={openDocCfg} variant="outline" data-testid="doc-config-btn" className="border-dusty-300 text-dusty-700 hover:bg-dusty-50">
                <FileText className="h-4 w-4 mr-1.5" /> Documenti richiesti
              </Button>
            )}
            {user.role === "owner" && (
              <Button onClick={openFormats} variant="outline" data-testid="doc-formats-btn" className="border-violet-300 text-violet-700 hover:bg-violet-50">
                <FileText className="h-4 w-4 mr-1.5" /> Format documenti
              </Button>
            )}
            <Button onClick={openNewManual} data-testid="add-manual-attestato-btn" className="bg-dusty-600 hover:bg-dusty-700 text-white">
              <Plus className="h-4 w-4 mr-1.5" /> Carica attestato manuale
            </Button>
          </div>
        )}
      </div>

      {isOwner && (
        <div data-testid="manual-attestati-section">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-display font-semibold text-slate-800">Attestati manuali</h2>
              <span className="text-xs font-normal text-slate-400">riconoscimento automatico dei dati · partner assegnato incluso ({manualItems.length})</span>
            </div>
            {manualItems.length > 0 && (
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input value={mSearch} onChange={(e) => setMSearch(e.target.value)} data-testid="manual-search"
                  placeholder="Cerca per corsista, corso, azienda, partner…" className="pl-9 bg-white h-9" />
              </div>
            )}
          </div>
          {(() => {
            const q = mSearch.trim().toLowerCase();
            const filteredManual = !q ? manualItems : manualItems.filter((m) =>
              [m.corsista_nome, m.codice_fiscale, m.corso_nome, m.azienda_nome, m.partner_nome]
                .some((v) => (v || "").toLowerCase().includes(q)));
            return manualItems.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/50 py-8 text-center text-sm text-slate-400" data-testid="manual-empty">
              Nessun attestato manuale. Usa «Carica attestato manuale»: la piattaforma leggerà da sola i dati dal file.
            </div>
          ) : filteredManual.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/50 py-8 text-center text-sm text-slate-400" data-testid="manual-no-results">
              Nessun attestato manuale corrisponde a «{mSearch}».
            </div>
          ) : (
            <PartnerGroups groups={groupByPartnerAzienda(filteredManual)} keyPrefix="mp" render={renderManualCard} forceOpen={!!q} />
          );
          })()}
        </div>
      )}

      {isOwner && (
        <div className="flex items-center gap-2 pt-2">
          <h2 className="text-lg font-display font-semibold text-slate-800">Attestati da ordini</h2>
          <span className="text-xs font-normal text-slate-400">raggruppati per partner e per azienda · apri per vedere i dettagli</span>
        </div>
      )}

      <div className="space-y-3" data-testid="attestati-toolbar">
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={attSearch} onChange={(e) => setAttSearch(e.target.value)} data-testid="attestati-search"
            placeholder="Cerca per corsista, corso, azienda o codice fiscale…" className="pl-9 bg-white" />
          {attSearch && (
            <button onClick={() => setAttSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="attestati-search-clear">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2 items-center" data-testid="attestati-tags">
          {ATT_TAGS.map(([key, label]) => {
            const active = attTag === key;
            const cls = active
              ? (key === "scaduto" ? "bg-rose-600 text-white border-rose-600"
                : key === "scadenza" ? "bg-amber-500 text-white border-amber-500"
                : key === "valido" ? "bg-emerald-600 text-white border-emerald-600"
                : "bg-dusty-600 text-white border-dusty-600")
              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50";
            return (
              <button key={key} type="button" onClick={() => setAttTag(key)} data-testid={`attestati-tag-${key}`}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${cls}`}>
                {label} <span className="opacity-70">({tagCounts[key] || 0})</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-3" data-testid="attestati-groups">
        {filteredItems.length === 0 ? (
          <div className="text-slate-400 col-span-full py-16 text-center" data-testid="attestati-empty">
            {items.length === 0
              ? (isOwner ? "Nessun attestato caricato. Caricali dagli ordini dei partner." : "Non ci sono ancora attestati disponibili. Ti avviseremo appena saranno pronti.")
              : "Nessun attestato corrisponde ai filtri selezionati."}
          </div>
        ) : isPartner ? (
          groupedByAzienda.map((g) => (
            <AziendaGroup key={g.azienda} azienda={g.azienda} items={g.items} groupKey={`az:${g.azienda}`} forceOpen={expandAll} />
          ))
        ) : (
          <PartnerGroups groups={groupedByPartner} keyPrefix="p" render={renderCard} forceOpen={expandAll} />
        )}
      </div>

      <Dialog open={mDialog} onOpenChange={(v) => { setMDialog(v); if (!v) { setMForm(emptyM); setMEditId(null); } }}>
        <DialogContent className="bg-white max-w-lg" data-testid="manual-attestato-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{mEditId ? "Modifica attestato manuale" : "Carica attestato manuale"}</DialogTitle>
            <DialogDescription>Allega il file: la piattaforma riconosce da sola corsista, codice fiscale, data di rilascio e scadenza. Puoi modificare i dati e assegnare un partner (che lo vedrà nella sua area).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1 max-h-[65vh] overflow-y-auto pr-1">
            <div>
              <Label>Attestato (PDF/immagine) {mEditId ? "" : "*"}</Label>
              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                <label className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer" data-testid="manual-file-label">
                  <Upload className="h-4 w-4" /> {mForm.file ? "Cambia file" : (mEditId ? "Sostituisci file" : "Scegli file")}
                  <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp" data-testid="manual-file-input"
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onManualFile(f); }} />
                </label>
                {mForm.file && <span className="text-xs text-slate-600 truncate inline-flex items-center gap-1"><FileText className="h-3.5 w-3.5" />{mForm.file.name}</span>}
                {analyzing && <span className="text-xs text-dusty-700 inline-flex items-center gap-1" data-testid="manual-analyzing"><Sparkles className="h-3.5 w-3.5 animate-pulse" /> Riconoscimento dati in corso…</span>}
              </div>
              {!mEditId && <p className="text-[11px] text-slate-400 mt-1 inline-flex items-center gap-1"><Sparkles className="h-3 w-3" /> I campi qui sotto si compilano da soli dopo il caricamento — controllali e correggili se serve.</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Corsista</Label><Input value={mForm.corsista_nome} onChange={(e) => setMForm({ ...mForm, corsista_nome: e.target.value })} placeholder="Nome e cognome" className="mt-1.5" data-testid="manual-corsista" /></div>
              <div><Label>Codice fiscale</Label><Input value={mForm.codice_fiscale} onChange={(e) => setMForm({ ...mForm, codice_fiscale: e.target.value })} placeholder="Codice fiscale" className="mt-1.5" data-testid="manual-cf" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Corso</Label><Input value={mForm.corso_nome} onChange={(e) => setMForm({ ...mForm, corso_nome: e.target.value })} placeholder="Nome del corso" className="mt-1.5" data-testid="manual-corso" /></div>
              <div><Label>Azienda</Label><Input value={mForm.azienda_nome} onChange={(e) => setMForm({ ...mForm, azienda_nome: e.target.value })} placeholder="Azienda (facoltativo)" className="mt-1.5" data-testid="manual-azienda" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data di rilascio</Label><Input type="date" value={mForm.data_rilascio} onChange={(e) => setMForm({ ...mForm, data_rilascio: e.target.value })} className="mt-1.5" data-testid="manual-rilascio" /></div>
              <div><Label>Scadenza</Label><Input type="date" value={mForm.scadenza} onChange={(e) => setMForm({ ...mForm, scadenza: e.target.value })} className="mt-1.5" data-testid="manual-scadenza" /></div>
            </div>
            <div>
              <Label>Partner di riferimento (facoltativo)</Label>
              <Select value={mForm.partner_id} onValueChange={(v) => setMForm({ ...mForm, partner_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="manual-partner"><SelectValue placeholder="Nessuno (solo interno)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno (solo interno)</SelectItem>
                  {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400 mt-1">Se assegni un partner, lo vedrà tra «I Miei Attestati» e potrà scaricarlo.</p>
            </div>
            <div>
              <Label>Allegati aggiuntivi (facoltativi)</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <label className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer" data-testid="manual-allegati-label">
                  <Paperclip className="h-4 w-4" /> Aggiungi file
                  <input type="file" multiple className="hidden" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx" data-testid="manual-allegati-input"
                    onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; setMForm((m) => ({ ...m, allegati: [...m.allegati, ...fs] })); }} />
                </label>
                <span className="text-xs text-slate-400">Nessun obbligo di caricare tutti i documenti</span>
              </div>
              {mForm.allegati.length > 0 && (
                <div className="mt-2 space-y-1">
                  {mForm.allegati.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-sm bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5" data-testid={`manual-allegato-pending-${i}`}>
                      <span className="inline-flex items-center gap-1.5 text-slate-600 truncate"><FileText className="h-3.5 w-3.5 shrink-0" />{f.name}</span>
                      <button type="button" onClick={() => setMForm((m) => ({ ...m, allegati: m.allegati.filter((_, k) => k !== i) }))} className="text-slate-400 hover:text-rose-500"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMDialog(false); setMForm(emptyM); setMEditId(null); }}>Annulla</Button>
            <Button onClick={submitManual} disabled={mBusy || analyzing} data-testid="manual-submit" className="bg-dusty-600 hover:bg-dusty-700 text-white">{mBusy ? "Salvo…" : (mEditId ? "Salva modifiche" : "Carica attestato")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={docCfgOpen} onOpenChange={(v) => { if (!v) setDocCfgOpen(false); }}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="doc-config-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Documenti richiesti per tipo di corso</DialogTitle>
            <DialogDescription>Definisci quali documenti firmati il partner deve caricare per sbloccare il download dell'attestato, in base alla tipologia del corso.</DialogDescription>
          </DialogHeader>
          {docCfg && (
            <div className="space-y-5 py-1">
              {CATS.map(([cat, label]) => (
                <div key={cat} className="rounded-lg border border-slate-200 p-3" data-testid={`doc-config-cat-${cat}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-slate-800">{label}</span>
                    <button type="button" onClick={() => addDoc(cat)} className="text-xs text-dusty-700 hover:underline inline-flex items-center gap-1" data-testid={`doc-config-add-${cat}`}>
                      <Plus className="h-3.5 w-3.5" /> Aggiungi documento
                    </button>
                  </div>
                  <div className="space-y-2">
                    {(docCfg[cat] || []).map((d, i) => (
                      <div key={i} className="flex items-center gap-2" data-testid={`doc-config-row-${cat}-${i}`}>
                        <Input value={d.label} onChange={(e) => setDocLabel(cat, i, e.target.value)} placeholder="Nome documento" className="bg-white h-9" data-testid={`doc-config-input-${cat}-${i}`} />
                        {d.template_path ? (
                          <span className="flex items-center gap-1 shrink-0">
                            <a href={fileUrl(d.template_path)} target="_blank" rel="noreferrer" title={d.template_name || "Modulo"} className="text-dusty-600 hover:text-dusty-700 inline-flex items-center gap-0.5 text-xs max-w-[120px] truncate" data-testid={`doc-config-template-${cat}-${i}`}><FileText className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{d.template_name || "modulo"}</span></a>
                            <button type="button" onClick={() => removeTemplate(cat, i)} className="text-slate-400 hover:text-rose-500 shrink-0" title="Rimuovi modulo" data-testid={`doc-config-template-remove-${cat}-${i}`}><X className="h-3.5 w-3.5" /></button>
                          </span>
                        ) : (
                          <>
                            <input type="file" className="hidden" id={`tpl-${cat}-${i}`} data-testid={`doc-config-template-input-${cat}-${i}`}
                              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadTemplate(cat, i, f); }} />
                            <label htmlFor={`tpl-${cat}-${i}`} className="shrink-0 cursor-pointer text-xs text-slate-500 hover:text-dusty-700 border border-slate-200 rounded-md px-2 h-9 flex items-center gap-1" title="Carica modulo da firmare" data-testid={`doc-config-template-btn-${cat}-${i}`}><Upload className="h-3.5 w-3.5" /> Modulo</label>
                          </>
                        )}
                        <button type="button" onClick={() => removeCfgDoc(cat, i)} className="text-slate-400 hover:text-rose-500 shrink-0" title="Rimuovi" data-testid={`doc-config-remove-${cat}-${i}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                    {(docCfg[cat] || []).length === 0 && <div className="text-xs text-slate-400">Nessun documento richiesto per questa tipologia.</div>}
                  </div>
                </div>
              ))}
              <p className="text-[11px] text-slate-400">Rinominare un documento non cancella i file già caricati. La rimozione di un documento lo esclude dai controlli futuri.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocCfgOpen(false)}>Annulla</Button>
            <Button onClick={saveDocCfg} disabled={docCfgBusy} data-testid="doc-config-save" className="bg-dusty-600 hover:bg-dusty-700 text-white">{docCfgBusy ? "Salvo…" : "Salva configurazione"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={fmtOpen} onOpenChange={setFmtOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="doc-formats-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Format documenti</DialogTitle>
            <DialogDescription>Crea modelli di documento con testo fisso e segnaposto (es. {"{{nome}}"}). Genera un PDF compilato quando serve.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {formats.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Format esistenti</Label>
                {formats.map((f) => (
                  <div key={f.id} className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid={`fmt-row-${f.id}`}>
                    <span className="text-slate-700"><span className="font-medium">{f.nome}</span> <span className="text-slate-400">· {f.categoria}</span></span>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openGen(f)} data-testid={`fmt-gen-${f.id}`}><Download className="h-3.5 w-3.5 mr-1" />Genera</Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => editFmt(f)} data-testid={`fmt-edit-${f.id}`}><Pencil className="h-3.5 w-3.5 text-slate-500" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => delFmt(f.id)} data-testid={`fmt-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-3 space-y-2">
              <Label className="text-violet-800 font-semibold text-sm">{fmtEditId ? "Modifica format" : "Nuovo format"}</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input value={fmtForm.nome} onChange={(e) => setFmtForm({ ...fmtForm, nome: e.target.value })} placeholder="Nome (es. Verbale finale)" className="bg-white h-9" data-testid="fmt-nome" />
                <Select value={fmtForm.categoria} onValueChange={(v) => setFmtForm({ ...fmtForm, categoria: v })}>
                  <SelectTrigger className="bg-white h-9" data-testid="fmt-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tutte">Tutte le modalità</SelectItem>
                    <SelectItem value="aula">Aula</SelectItem>
                    <SelectItem value="videoconferenza">Videoconferenza</SelectItem>
                    <SelectItem value="elearning">FAD asincrona</SelectItem>
                    <SelectItem value="consulenza">Consulenza</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap gap-1">
                {FMT_FIELDS.map(([k, lbl]) => (
                  <button key={k} type="button" onClick={() => insertField(k)} data-testid={`fmt-field-${k}`}
                    className="text-[11px] px-2 py-0.5 rounded-full border border-violet-200 bg-white text-violet-700 hover:bg-violet-100">+ {lbl}</button>
                ))}
              </div>
              <Textarea value={fmtForm.corpo} onChange={(e) => setFmtForm({ ...fmtForm, corpo: e.target.value })} rows={7} className="bg-white font-mono text-xs" placeholder={"Es. Si attesta che {{nome}} {{cognome}} ha frequentato il corso {{corso}} in data {{data}}."} data-testid="fmt-corpo" />
              <div className="flex justify-end gap-2">
                {fmtEditId && <Button size="sm" variant="outline" className="h-8" onClick={() => { setFmtForm(emptyFmt); setFmtEditId(null); }}>Annulla modifica</Button>}
                <Button size="sm" onClick={saveFmt} className="bg-violet-600 hover:bg-violet-700 text-white h-8" data-testid="fmt-save">{fmtEditId ? "Aggiorna" : "Crea format"}</Button>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setFmtOpen(false)}>Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!genFor} onOpenChange={(v) => !v && setGenFor(null)}>
        <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="doc-format-gen-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Genera: {genFor?.nome}</DialogTitle>
            <DialogDescription>Compila i campi da inserire nel documento, poi scarica il PDF.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 py-1">
            {FMT_FIELDS.map(([k, lbl]) => (
              <div key={k}>
                <Label className="text-xs text-slate-500">{lbl}</Label>
                <Input value={genFields[k] || ""} onChange={(e) => setGenFields({ ...genFields, [k]: e.target.value })} className="h-9 mt-1" data-testid={`gen-field-${k}`} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenFor(null)}>Annulla</Button>
            <Button onClick={generatePdf} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="gen-pdf-btn"><Download className="h-4 w-4 mr-1.5" />Genera PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
